import type { DueOn } from './paper'
import { longDate, MONTHS } from './paper'
import { daysBetween } from './estimates'

/**
 * WHAT IS OWED, AND WHAT IS LATE.
 *
 * PAY-01. Money in this product is a column on a job row, which is the wrong
 * shape for the only question this screen exists to answer: what is out there,
 * how much of it is late, and who do I chase this morning. A column can be
 * sorted; it cannot be totalled, filtered by lateness, or opened on a phone in
 * a driveway as a list of four names.
 *
 * NOTHING HERE COLLECTS MONEY. Payments in this trade are handed over outside
 * the app, which is why lib/paper.ts keeps a list of what actually arrived
 * rather than a paid flag. This screen reads that list. It never charges
 * anybody, never asks anybody to pay, and never marks a thing as settled that
 * a person did not enter.
 *
 * WHAT THE SPEC ASKS FOR THAT NOTHING HERE CAN ANSWER.
 *
 *   Sent. Nothing in this app has ever sent a message, so there is no sent
 *   state and no unsent one either. What there is instead is a draft: an
 *   invoice with no document number on it has not been issued.
 *
 *   Pending. That is a payment submitted to a provider and not yet settled.
 *   There is no provider. Every payment on here is one a person wrote down.
 *
 *   Credits and refunds. PAY-03, and there is no ledger to correct.
 *
 * AND ONE THING WORTH SAYING OUT LOUD. An invoice in this product is the same
 * document with its kind changed, not a snapshot taken at issue, so editing
 * the quote afterwards changes the invoice. The design package calls that out
 * by name. Until there is a real issued-document record it is a fact about
 * this screen, so this screen says it.
 */

/** The money the job list already carries. Same shape as db.JobMoney. */
export interface BillMoney {
  kind: 'quote' | 'invoice'
  number: string
  dated: string
  due: DueOn
  dueDate: string
  deposit: number
  lastPaid: string
  total: number
  paid: number
  balance: number
}

export interface BillRef {
  id: string
  customer: string
  address: string
  name: string
  money?: BillMoney
  /**
   * THE DAY THE INSTALL WAS MARKED FINISHED, or empty. See lib/visits.ts.
   *
   * This did not exist until a stop could be started and finished on Today.
   * It is what turns due on completion from a sentence on a page into a date,
   * and it is why an invoice that has been earned no longer sits in the middle
   * of the owed list looking the same as one for a job nobody has driven to.
   */
  finishedOn?: string
}

/*
 * EVERY FIELD ON THAT INTERFACE IS REQUIRED, AND A SAVED JOB IS NOT.
 *
 * A job written before due dates existed, or by an older build, or restored
 * from a partial save, has a money object with no dueDate and no number on
 * it. TypeScript believes the interface; localStorage does not. So the whole
 * Invoices screen white-screened on undefined.slice, and the crash reached
 * Stock, Costs and Settings as well because the whole workspace unmounted.
 *
 * These two readers are the only place either field is touched. A missing due
 * date reads as no due date, which is already a first-class answer in this
 * file and the one that costs money if it is guessed at. A missing number
 * reads as not issued, which is what an unnumbered document is.
 */
const dueOf = (m: BillMoney): string => (typeof m.dueDate === 'string' ? m.dueDate : '')
const numberOf = (m: BillMoney): string => (typeof m.number === 'string' ? m.number : '')

/** A quote is not a bill. Only a document somebody turned into one is. */
export const isInvoice = (j: BillRef): boolean => j.money?.kind === 'invoice'

/*
 * FOUR STATES, AND EVERY ONE OF THEM HAS SOMETHING BEHIND IT.
 *
 * The spec lists six. Two of them, sent and pending, describe events this
 * product has no record of, and a filter that can never match anything is a
 * lie about the shape of the work. Partial is not a state here either: it is
 * true of a due invoice and an overdue one alike, so it belongs on the row
 * rather than in the tab strip.
 */
export type BillState = 'draft' | 'overdue' | 'due' | 'paid'

export const BILL_STATES: { id: BillState; label: string; short: string }[] = [
  { id: 'draft', label: 'Draft', short: 'Draft' },
  { id: 'due', label: 'Owed', short: 'Owed' },
  { id: 'overdue', label: 'Past due', short: 'Past due' },
  { id: 'paid', label: 'Paid', short: 'Paid' },
]

/** True only when a date was named AND has already gone by. */
export function isPastDue(m: BillMoney, today: string): boolean {
  if (m.balance <= 0) return false
  if (m.due !== 'date') return false
  const d = dueOf(m).slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return false
  return d < today
}

export function billState(j: BillRef, today: string): BillState {
  const m = j.money
  if (!m) return 'draft'
  // Not issued. docNumber is empty until the document has a number on it, and
  // a bill nobody has numbered is not a bill anybody has been given.
  if (!numberOf(m).trim()) return 'draft'
  if (m.balance <= 0 && m.total > 0) return 'paid'
  if (isPastDue(m, today)) return 'overdue'
  return 'due'
}

/** How late, in days. Null whenever it is not late, which includes on time. */
export function daysLate(m: BillMoney, today: string): number | null {
  if (!isPastDue(m, today)) return null
  return daysBetween(dueOf(m).slice(0, 10), today)
}

/**
 * SEPTEMBER 14, AND THE YEAR ONLY WHEN IT IS NOT THIS ONE.
 *
 * The printed document says September 14, 2026 and should: it is a contract
 * and it outlives the year. A row on a list of what is owed this morning does
 * not, and the four extra characters were pushing the sentence onto a second
 * line at 390 for no information anybody needed.
 */
export function dueShort(iso: string, today: string): string {
  const d = (iso ?? '').slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return ''
  const [y, m, day] = d.split('-').map(Number)
  const name = `${MONTHS[m - 1]} ${day}`
  return String(y) === today.slice(0, 4) ? name : `${name}, ${y}`
}

/**
 * WHEN IT IS DUE, SAID PLAINLY, INCLUDING WHEN NOBODY HAS SAID.
 *
 * Three answers, and the third one is the one that costs money: a document set
 * to be due on a date that nobody ever filled in. It reads as scheduled and is
 * not, so it is never quietly folded in with the ones that are.
 */
export function dueWording(m: BillMoney, today = ''): string {
  if (m.due === 'completion') return 'Due when the job is finished'
  // The long form is kept for anywhere without a today to compare against,
  // which is what the printed document wants anyway.
  const when = today ? dueShort(dueOf(m), today) : longDate(dueOf(m))
  return when ? `Due ${when}` : 'No due date on it'
}

export const hasNoDueDate = (m: BillMoney): boolean =>
  m.due === 'date' && !/^\d{4}-\d{2}-\d{2}$/.test(dueOf(m).slice(0, 10))

/**
 * THE ONE LINE UNDER THE NAME.
 *
 * Part paid is said here rather than in a tab, because half of two thousand
 * dollars owed is a different phone call from all of it and the same filter.
 */
/** Earned and unpaid: the install is done and the balance is still out. */
export const isEarned = (j: BillRef): boolean =>
  !!j.finishedOn && (j.money?.balance ?? 0) > 0 && j.money?.due === 'completion'

export function said(j: BillRef, today: string): string {
  const m = j.money
  if (!m) return 'Nothing priced on it.'
  const state = billState(j, today)
  if (state === 'draft') return 'No number on it yet. It has not been issued.'
  if (state === 'paid') {
    const when = m.lastPaid ? dueShort(m.lastPaid, today) : ''
    return when ? `Paid in full ${when}.` : 'Paid in full.'
  }

  const part = m.paid > 0 ? ` Part paid.` : ''
  if (state === 'overdue') {
    const n = daysLate(m, today)
    const how = n === null || n <= 0 ? '' : n === 1 ? ' by a day' : n < 14 ? ` by ${n} days` : ` by ${Math.floor(n / 7)} weeks`
    return `Past due${how}.${part}`
  }
  // Owed, and the reason it is not late is worth reading: a job nobody has
  // finished and a document nobody dated are not the same situation.
  /*
   * A JOB THAT IS ACTUALLY FINISHED READS DIFFERENTLY FROM ONE THAT IS NOT.
   *
   * Due when the job is finished is a promise; finished on the twelfth with a
   * balance still out is a phone call. It is not called past due, because how
   * many days after completion counts as late is a policy nobody has set in
   * this product and inventing one would be putting words in a contractor's
   * mouth. It sorts to the top of the owed group instead.
   */
  if (isEarned(j)) {
    return `Finished ${dueShort(j.finishedOn ?? '', today)}. The balance is due.${part}`
  }
  if (m.deposit > 0 && m.paid <= 0) return `${dueWording(m, today)}. The deposit has not arrived.`
  return `${dueWording(m, today)}.${part}`
}

/*
 * THE ORDER: LATE, THEN OWED, THEN NOT YET ISSUED, THEN SETTLED.
 *
 * Within late, the latest first. Within owed, the soonest due first, and the
 * ones with no due date at all last inside their own group rather than sorted
 * as though a missing date were a distant one.
 */
const RANK: Record<BillState, number> = { overdue: 0, due: 1, draft: 2, paid: 3 }

export function byUrgency(rows: BillRef[], today: string): BillRef[] {
  return [...rows].sort((a, b) => {
    const sa = billState(a, today)
    const sb = billState(b, today)
    if (RANK[sa] !== RANK[sb]) return RANK[sa] - RANK[sb]
    const ma = a.money
    const mb = b.money
    if (!ma || !mb) return 0
    if (sa === 'overdue') return (daysLate(mb, today) ?? 0) - (daysLate(ma, today) ?? 0)
    if (sa === 'due') {
      // Earned first: the work is done and the money is out, which is the
      // strongest claim on the owed pile short of a date that has gone by.
      const ea = isEarned(a)
      const eb = isEarned(b)
      if (ea !== eb) return ea ? -1 : 1
      if (ea && eb) return (a.finishedOn ?? '') < (b.finishedOn ?? '') ? -1 : 1
      const da = hasNoDueDate(ma) || ma.due === 'completion' ? '' : dueOf(ma).slice(0, 10)
      const db = hasNoDueDate(mb) || mb.due === 'completion' ? '' : dueOf(mb).slice(0, 10)
      if (!da && !db) return mb.balance - ma.balance
      if (!da) return 1
      if (!db) return -1
      return da < db ? -1 : da > db ? 1 : 0
    }
    if (sa === 'paid') {
      // Most recently settled first: the only reason to look is what just
      // happened.
      return (mb.lastPaid || '') < (ma.lastPaid || '') ? -1 : 1
    }
    return mb.balance - ma.balance
  })
}

/** Everything still owed on issued invoices. The number of the screen. */
export const owed = (rows: BillRef[], today: string): number =>
  rows
    .filter((j) => billState(j, today) === 'overdue' || billState(j, today) === 'due')
    .reduce((n, j) => n + (j.money?.balance ?? 0), 0)

/** And how much of it is late, which is the half that needs a phone today. */
export const late = (rows: BillRef[], today: string): number =>
  rows
    .filter((j) => billState(j, today) === 'overdue')
    .reduce((n, j) => n + (j.money?.balance ?? 0), 0)

/** Deposits asked for on issued invoices where no money has arrived at all. */
export const depositsOut = (rows: BillRef[], today: string): number =>
  rows
    .filter((j) => billState(j, today) !== 'paid' && billState(j, today) !== 'draft')
    .filter((j) => (j.money?.deposit ?? 0) > 0 && (j.money?.paid ?? 0) <= 0)
    .reduce((n, j) => n + (j.money?.deposit ?? 0), 0)

export const NOT_TRACKED: string[] = [
  'Whether an invoice was sent. Nothing in this app sends anything, so there is no sent state and no unsent one.',
  'Payments in progress. Every payment on here is one somebody wrote down after it arrived, and nothing here charges a card.',
  'Refunds and credits. There is no ledger to correct yet.',
  'How late a finished job is. Marking a stop finished on Today records the day, but nobody has set how many days after that counts as past due, so these sort to the top of the owed pile rather than being flagged.',
  'An invoice is the same document with its kind changed rather than a copy taken when it was issued, so editing the quote changes it.',
]
